import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowLeft, KeyRound } from 'lucide-react';
import LoadingScreen from '../components/LoadingScreen';

// Small loading spinner for buttons that matches the LoadingScreen style
const LoadingSpinner = ({ size = 5, className = '' }) => (
  <div className={`w-${size} h-${size} border-2 border-t-transparent border-white rounded-full animate-spin mr-2 ${className}`}></div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errors, setErrors] = useState({
    email: ''
  });
  const [generalError, setGeneralError] = useState('');
  const navigate = useNavigate();

  const validate = () => {
    const newErrors = {
      email: ''
    };
    let isValid = true;

    // First check if field is empty
    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    }

    // Then check validity if not empty
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    setErrors(newErrors);

    // Focus on the field with an error
    if (!isValid && document.getElementById('email')) {
      document.getElementById('email').focus();
    }

    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate()) return;
    setLoading(true);
    setGeneralError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE}/signup/forgotpassword`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.status === 404) {
        setGeneralError('User not found');
        // Redirect to signup after 5 seconds
        setTimeout(() => {
          navigate('/signup');
        }, 5000);
        return;
      }

      if (response.status === 402) {
        setGeneralError(
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-[var(--text-primary)] mb-4">You've signed up with Google! Please use the "Continue with Google" option to log in.</p>
            <button
              onClick={() => navigate('/login')}
              className="text-[var(--accent-primary)] hover:text-[var(--hover-dark)] text-sm font-medium"
            >
              Go to login
            </button>
          </div>
        );
        return;
      }

      if (response.ok) {
        setEmailSent(true);
      } else {
        const data = await response.json();
        setGeneralError(data.message || 'Something went wrong');
      }
    } catch (err) {
      setGeneralError('Unable to connect to the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    setErrors({ email: '' });
    setGeneralError('');
  };

  return (
    <div 
      className="max-w-sm w-full sm:max-w-md p-6 my-8 bg-white rounded-xl border border-[var(--border-color)] space-y-2"
      style={{ boxShadow: 'var(--card-shadow)' }}
    >
      <div className="flex flex-col items-center mb-6">
        <KeyRound className="w-8 h-8 text-[var(--accent-primary)] mb-4" strokeWidth={1} />
        <h2 className="text-2xl font-bold text-[var(--text-primary)]">Reset Password</h2>
      </div>
      
      {generalError && (
        <div className={`mb-6 ${typeof generalError === 'string' ? 'p-4 rounded bg-[var(--card-bg)] border border-[var(--border-color)] shadow-sm' : ''}`}>
          {typeof generalError === 'string' ? (
            <div className="flex items-center gap-3">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="var(--accent-primary)"/>
              </svg>
              <span className="text-[var(--text-primary)] text-sm">{generalError}</span>
            </div>
          ) : (
            generalError
          )}
        </div>
      )}
      
      {emailSent ? (
        <div className="space-y-6">
          <div className="bg-[var(--hover-light)] rounded-lg p-6">
            <div className="flex justify-center mb-4">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM19.6 8.25L12.53 12.67C12.21 12.87 11.79 12.87 11.47 12.67L4.4 8.25C4.15 8.09 4 7.82 4 7.53C4 6.86 4.73 6.46 5.3 6.81L12 11L18.7 6.81C19.27 6.46 20 6.86 20 7.53C20 7.82 19.85 8.09 19.6 8.25Z" fill="var(--accent-secondary)"/>
              </svg>
            </div>
            <p className="text-[var(--text-primary)] text-center mb-2 font-medium">
              Check your email
            </p>
            <p className="text-[var(--text-secondary)] text-sm text-center">
              We've sent password reset instructions to your email
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            className="w-fit flex items-center px-4 py-2 font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="relative">
              <input
                type="text"
                id="email"
                value={email}
                onChange={handleChange}
                disabled={loading}
                className={`w-full p-2 pl-10 border rounded bg-[var(--card-bg)] focus:outline-none ${
                  errors.email 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
                placeholder="Enter your email"
              />
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.email && (
              <p className="mt-1 text-red-500 text-sm">{errors.email}</p>
            )}
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] transition disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <LoadingSpinner />
                  Processing...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-fit flex items-center px-4 py-2 font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Return
            </button>
          </div>
        </form>
      )}
    </div>
  );
} 